// Tone System - AI Reading Voice Preferences
// Similar to useTheme.js, provides selectable tones for AI responses

import React, { createContext, useContext, useState, useEffect } from 'react';

// Tone definitions with emojis and prompt prefixes
export const TONES = {
    mystical: {
        id: 'mystical',
        name: 'Mystical Oracle',
        emoji: '✨',
        description: 'Ethereal and spiritual',
        promptPrefix: 'You are a mystical tarot reader. Keep the tone mystical, ethereal, and supportive. Use evocative, poetic language that feels like ancient wisdom.',
    },
    friendly: {
        id: 'friendly',
        name: 'Friendly Guide',
        emoji: '🤗',
        description: 'Warm and approachable',
        promptPrefix: 'You are a warm, friendly tarot guide. Use casual, supportive language that feels like advice from a trusted friend. Be encouraging and relatable.',
    },
    direct: {
        id: 'direct',
        name: 'Straight-Forward',
        emoji: '🎯',
        description: 'Clear and concise',
        promptPrefix: 'You are a direct tarot reader. Be clear and concise, avoiding flowery language. Get straight to the point with practical insights.',
    },
    academic: {
        id: 'academic',
        name: 'Scholarly Sage',
        emoji: '📚',
        description: 'Educational focus',
        promptPrefix: 'You are a knowledgeable tarot scholar. Focus on symbolism, historical context, and deeper meanings. Provide educational insights while remaining accessible.',
    },
    playful: {
        id: 'playful',
        name: 'Playful Mystic',
        emoji: '🎭',
        description: 'Fun and lighthearted',
        promptPrefix: 'You are a playful tarot reader with a sense of humor. Keep readings uplifting and fun while still providing meaningful guidance. Add a touch of wit.',
    },
};

// Create context
const ToneContext = createContext(null);
const DEFAULT_TONE = 'direct';

// Provider component
export const ToneProvider = ({ children }) => {
    const [currentTone, setCurrentTone] = useState(DEFAULT_TONE);

    // Load tone from localStorage on mount
    useEffect(() => {
        const savedTone = localStorage.getItem('cosmicTone');
        const userSelectedTone = localStorage.getItem('cosmicToneUserSelected') === 'true';

        if (savedTone && TONES[savedTone] && (userSelectedTone || savedTone !== 'mystical')) {
            setCurrentTone(savedTone);
        } else {
            setCurrentTone(DEFAULT_TONE);
        }
    }, []);

    // Save to localStorage when tone changes
    useEffect(() => {
        localStorage.setItem('cosmicTone', currentTone);
    }, [currentTone]);

    const setTone = (toneId) => {
        if (TONES[toneId]) {
            localStorage.setItem('cosmicToneUserSelected', 'true');
            setCurrentTone(toneId);
        }
    };

    const value = {
        currentTone,
        setTone,
        tone: TONES[currentTone],
        tones: TONES,
        // Convenience getter for prompt prefix
        promptPrefix: TONES[currentTone]?.promptPrefix || TONES[DEFAULT_TONE].promptPrefix,
    };

    return (
        <ToneContext.Provider value={value}>
            {children}
        </ToneContext.Provider>
    );
};

// Hook to use tone context
export const useTone = () => {
    const context = useContext(ToneContext);
    if (!context) {
        throw new Error('useTone must be used within a ToneProvider');
    }
    return context;
};

export default useTone;
